// =============================================================================
// MHV FETCH — Request deduplication and micro-caching
// Prevents duplicate in-flight requests and provides short TTL cache for GETs.
// Load AFTER mhv-config.js. Exposes window.mhvFetch().
// =============================================================================

(function() {
  'use strict';

  // In-flight request cache - stores promises that resolve to {ok, status, data}
  var inFlightRequests = new Map();

  // Response micro-cache for GET requests - short TTL to prevent burst re-fetches
  var responseCache = new Map();
  var CACHE_TTL_MS = 500; // 500ms micro-cache for GET requests

  // Generate cache key from request details
  function getCacheKey(url, options) {
    options = options || {};
    var method = (options.method || 'GET').toUpperCase();
    var body = options.body || '';
    return method + ':' + url + ':' + body;
  }

  // Create a synthetic response object from cached data
  function createSyntheticResponse(cached) {
    return {
      ok: cached.ok,
      status: cached.status,
      json: function() { return Promise.resolve(cached.data); },
      text: function() { return Promise.resolve(JSON.stringify(cached.data)); }
    };
  }

  // Clean expired cache entries
  function cleanExpiredCache() {
    var now = Date.now();
    for (var entry of responseCache.entries()) {
      if (now - entry[1].timestamp > CACHE_TTL_MS) {
        responseCache.delete(entry[0]);
      }
    }
  }

  // Periodically clean cache
  setInterval(cleanExpiredCache, 5000);

  /**
   * Deduplicated fetch wrapper
   * @param {string} url - Request URL
   * @param {object} options - Fetch options
   * @param {object} mhvOptions - MHV-specific options
   * @param {boolean} mhvOptions.useCache - Whether to use micro-cache for GET (default: true)
   * @param {boolean} mhvOptions.dedupe - Whether to deduplicate in-flight requests (default: true)
   */
  window.mhvFetch = async function(url, options, mhvOptions) {
    options = options || {};
    mhvOptions = mhvOptions || {};
    // API JSON is never something to reuse from the HTTP cache: a 304 hands back a
    // stale body (or, to a bare fetch, an empty one). The server says no-store too;
    // this covers a response cached before it did (2026-09-11: the registration
    // modal rendered its old layout from a pre-deploy cached context).
    if (!options.cache) options.cache = 'no-store';
    var useCache = mhvOptions.useCache !== undefined ? mhvOptions.useCache : true;
    var dedupe = mhvOptions.dedupe !== undefined ? mhvOptions.dedupe : true;
    var method = (options.method || 'GET').toUpperCase();
    var cacheKey = getCacheKey(url, options);

    // For GET requests, check micro-cache first
    if (method === 'GET' && useCache) {
      var cached = responseCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
        return Promise.resolve(createSyntheticResponse(cached));
      }
    }

    // Check if request is already in-flight
    if (dedupe && inFlightRequests.has(cacheKey)) {
      var cachedData = await inFlightRequests.get(cacheKey);
      return createSyntheticResponse(cachedData);
    }

    // "Browse as customer" override: authenticate as the target (or nobody, for guest),
    // bypassing the real session's key provisioning entirely.
    var browsingAs = window.MHVBrowseAs && window.MHVBrowseAs.active;
    var effectiveKey;
    if (browsingAs) {
      effectiveKey = window.MHVBrowseAs.guest ? null : (window.MHVBrowseAs.key || null);
    } else {
      // Wait for customer key provisioning before sending authenticated requests
      if (window.MHV_CUSTOMER_KEY_READY) {
        await window.MHV_CUSTOMER_KEY_READY;
      }
      effectiveKey = window.MHV_CUSTOMER_KEY;
    }

    // Inject customer API key header if available (for authenticated customer endpoints)
    if (effectiveKey) {
      options = Object.assign({}, options);
      options.headers = Object.assign({}, options.headers || {}, {
        'X-Customer-Key': effectiveKey
      });
    }

    // Create a promise that resolves to the parsed data (not the Response)
    var dataPromise = fetch(url, options)
      .then(async function(response) {
        var data = await response.json().catch(function() { return null; });

        var cacheEntry = {
          ok: response.ok,
          status: response.status,
          data: data,
          timestamp: Date.now()
        };

        // Cache successful GET responses
        if (method === 'GET' && response.ok && useCache && data) {
          responseCache.set(cacheKey, cacheEntry);
        }

        return cacheEntry;
      })
      .finally(function() {
        inFlightRequests.delete(cacheKey);
      });

    // Store in in-flight cache
    if (dedupe) {
      inFlightRequests.set(cacheKey, dataPromise);
    }

    // Wait for data and return synthetic response
    var resolvedData = await dataPromise;
    return createSyntheticResponse(resolvedData);
  };

  // Expose cache clearing for testing/debugging
  window.mhvFetch.clearCache = function() {
    responseCache.clear();
    inFlightRequests.clear();
  };
})();
